import clsx from 'clsx';
import ControlHitArea from 'components/inputs/control-hit-area';
import { StockDot } from 'components/layout/modals/compatible-combinations-browser-modal';
import { basicProductTitle } from 'utils/basic-product-title';
import {
  AccessoryGroup,
  AccessoryOption,
  AccessoryPick,
  AccessoryPicks,
  accessoryOptionModelText,
  accessoryPairKey,
  hasAccessoryChoice,
  isSameAccessoryPick,
  selectedAccessoryOption,
  toAccessoryPick
} from 'utils/ducted-accessory-groups';
import { formatPrice } from 'utils/helpers';
import DuctedProductRow from './ducted-product-row';

type DuctedAccessoryRowsProps = Readonly<{
  groups: AccessoryGroup[];
  picks: AccessoryPicks;
  onPick: (accessoryTypeCode: string, pick: AccessoryPick) => void;
  radioName: string;
  disabled?: boolean;
}>;

type AccessoryRadioProps = Readonly<{
  name: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onSelect: () => void;
}>;

type AlternativeOptionLineProps = Readonly<{
  option: AccessoryOption;
  name: string;
  disabled: boolean;
  onSelect: () => void;
}>;

function AccessoryRadio({
  name,
  label,
  checked,
  disabled,
  onSelect
}: AccessoryRadioProps) {
  return (
    <ControlHitArea as='span' disabled={disabled}>
      <input
        type='radio'
        className='h-4 w-4 shrink-0 cursor-pointer text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50'
        name={name}
        aria-label={label}
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
      />
    </ControlHitArea>
  );
}

function AlternativeOptionLine({
  option,
  name,
  disabled,
  onSelect
}: AlternativeOptionLineProps) {
  const { product } = option.enriched;
  const modelText = accessoryOptionModelText(option);
  const title = basicProductTitle(product, modelText);
  return (
    <label
      className={clsx(
        'flex items-center gap-3 px-3 py-2 text-xs',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'
      )}
    >
      <AccessoryRadio
        name={name}
        label={modelText}
        checked={false}
        disabled={disabled}
        onSelect={onSelect}
      />
      <span className='flex min-w-0 grow items-center gap-2'>
        <span className='truncate font-medium text-gray-900'>{title}</span>
        {title !== modelText && (
          <span className='shrink-0 text-gray-500'>{modelText}</span>
        )}
        <StockDot product={product ?? undefined} />
      </span>
      <span
        className={clsx(
          'shrink-0 font-semibold',
          product?.isOnSale ? 'text-red-600' : 'text-gray-900'
        )}
      >
        {product && product.price != null ? formatPrice(product.price) : '—'}
      </span>
    </label>
  );
}

export default function DuctedAccessoryRows({
  groups,
  picks,
  onPick,
  radioName,
  disabled = false
}: DuctedAccessoryRowsProps) {
  const alignWithRadios = groups.some(hasAccessoryChoice);

  return (
    <div className='divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200'>
      {groups.map((group) => {
        const selected = selectedAccessoryOption(group, picks);
        const selectedModelText = accessoryOptionModelText(selected);

        if (!hasAccessoryChoice(group)) {
          return (
            <DuctedProductRow
              key={group.accessoryTypeCode}
              caption={group.accessoryTypeLabel}
              modelText={selectedModelText}
              product={selected.enriched.product}
              leading={
                alignWithRadios ? (
                  <span aria-hidden className='h-4 w-4 shrink-0' />
                ) : undefined
              }
            />
          );
        }

        const name = `${radioName}-${group.accessoryTypeCode}`;

        return (
          <div key={group.accessoryTypeCode} className='flex flex-col bg-white'>
            {group.options.map((option) => {
              const key = accessoryPairKey(option);
              const onSelect = () =>
                onPick(group.accessoryTypeCode, toAccessoryPick(option));

              if (!isSameAccessoryPick(option, selected)) {
                return (
                  <AlternativeOptionLine
                    key={key}
                    option={option}
                    name={name}
                    disabled={disabled}
                    onSelect={onSelect}
                  />
                );
              }

              return (
                <DuctedProductRow
                  key={key}
                  as={disabled ? 'div' : 'label'}
                  caption={group.accessoryTypeLabel}
                  modelText={selectedModelText}
                  product={option.enriched.product}
                  leading={
                    <AccessoryRadio
                      name={name}
                      label={selectedModelText}
                      checked
                      disabled={disabled}
                      onSelect={onSelect}
                    />
                  }
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
